import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isSameDay } from 'date-fns';
import type AbilitiesService from 'ember-can/services/abilities';
import type { Task } from 'ember-concurrency';
import { dropTask } from 'ember-concurrency';
import type ListItemCheckbox from 'garaje/components/list-item-checkbox';
import type EntryModel from 'garaje/models/entry';
import type LocationModel from 'garaje/models/location';
import type { Field } from 'garaje/models/location';
import type PrinterModel from 'garaje/models/printer';
import type ReservationModel from 'garaje/models/reservation';
import type SubscriptionModel from 'garaje/models/subscription';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type WindowLocationService from 'garaje/services/window-location';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import urlBuilder from 'garaje/utils/url-builder';
import zft from 'garaje/utils/zero-for-tests';
import { alias, reads, or } from 'macro-decorators';
import { defer } from 'rsvp';

interface EntryFeedItemComponentSignature {
  Args: {
    checkbox: ListItemCheckbox<unknown>;
    checkID: () => unknown;
    context: 'location' | 'property';
    deleteEntry: () => unknown;
    entry: EntryModel;
    eventReports: unknown[];
    fieldOptions: Field[];
    fullDate: boolean;
    hasPhoto: boolean;
    isSelected: boolean;
    location: LocationModel;
    loadEntries: Task<unknown, []>;
    printers: PrinterModel[];
    reprintBadge: (entry: EntryModel, printer: PrinterModel) => unknown;
    selectedDate: Date;
    showSignedOut: boolean;
    signOutTask: Task<unknown, []>;
    vrSubscription: SubscriptionModel;
    linkToRoute: string;
    renderStickyShadows?: boolean;
    userType: string;
  };
}

export default class EntryFeedItemComponent extends Component<EntryFeedItemComponentSignature> {
  @service declare abilities: AbilitiesService;
  @service declare ajaxFetch: AjaxFetchService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare windowLocation: WindowLocationService;
  @service declare workplaceMetrics: WorkplaceMetricsService;

  @tracked displayPrinterSelectorModal = false;

  @tracked itemFullNameWasTruncated = false;
  @tracked itemHostWasTruncated = false;

  tooltipDelay = zft(400);

  @alias('state.currentCompany') currentCompany!: StateService['currentCompany'];
  @alias('args.vrSubscription.canEnableBadgePrinting') canReprintBadge!: SubscriptionModel['canEnableBadgePrinting'];
  @reads('currentAdmin.canDeleteVisitor') canDeleteVisitor!: CurrentAdminService['canDeleteVisitor'];
  @or('canReferToEnvoy', 'canReprintBadge', 'canSignOutVisitor', 'canDeleteVisitor', 'canManageReservation')
  menuHasActions!: boolean;

  @or('args.entry.needsApprovalReview', 'args.entry.approvalWasDenied') visitorIsDisallowedAccess!: boolean;

  get reviewerName(): string | undefined {
    return this.args.entry.visitApproval?.screenerReviews.slice()[0]?.reviewerName;
  }

  get canManageReservation(): boolean {
    if (this.isForVisitor) {
      return false;
    }

    return this.abilities.can('edit reservation') || this.abilities.can('delete reservation');
  }

  // colspan for the denied message is equal the the number of shown fieldOptions minus 1 for the name field, which is already in the row and is not included in the colspan
  get deniedColspan(): number {
    return (this.args.fieldOptions ?? []).filter((field) => field.show).length - 1;
  }

  get canSignOutVisitor(): boolean {
    const entry = this.args.entry;

    // cannot sign out entry if it was not signed in
    if (!entry.signInTime) return false;

    // cannot sign out entry if it's already signed out
    if (entry.signedOutAt) return false;

    // cannot sign out entry is approval was denied
    if (entry.approvalWasDenied) return false;

    // otherwise, entry can be signed out if user has 'edit' ability for it
    return this.abilities.can('edit entry', entry);
  }

  get cannotReviewEntry(): boolean {
    return this.abilities.cannot('review entry-approval', {
      context: this.args.context ?? 'location',
      visitApproval: this.args.entry.visitApproval,
      parent: this.args.entry,
    });
  }

  get canInviteAgain(): boolean {
    return !this.args.entry.isFromEmployeeScreening && !this.args.entry.isFromWalkUp && !this.visitorIsDisallowedAccess;
  }

  get visitorEmail(): string | undefined {
    return this.getUserDataField(this.args.entry, 'Your Email Address');
  }

  get visitorFullName(): string | undefined {
    return this.getUserDataField(this.args.entry, 'Your Full Name');
  }

  get canReferToEnvoy(): boolean {
    return !!this.visitorEmail;
  }

  get referralCode(): string {
    return `c${this.currentCompany.id}u${this.currentAdmin.id}`;
  }

  get referralUrl(): string {
    return urlBuilder.signupReferralUrl(this.referralCode);
  }

  get isWorkplaceRoute(): boolean {
    return this.router.isActive('workplace');
  }

  get referralMailtoLink(): string {
    const subject = `Interested in ${this.currentCompany.name}'s visitor sign-in system?`;
    const bcc = 'referrals@envoy.com';
    const body = `

      Hi ${this.visitorFullName},

      When you signed in on the iPad today, you experienced Envoy — our digital sign in system.

      If you're interested in trying Envoy for yourself, just visit this link to sign up: ${this.referralUrl}

      Once you sign up, email referrals@envoy.com to connect with the Envoy team and claim your special referral discount.

      Best,
      ${this.currentAdmin.fullName}
      ${this.currentCompany.name}

    `
      .trim()
      .replace(/\n +/g, '\n')
      .replace(/\n/g, '%0D%0A');
    return `mailto:${this.visitorEmail}?subject=${subject}&bcc=${bcc}&body=${body}`;
  }

  get showInDate(): boolean {
    const selectedDate = this.args.selectedDate;
    const signedInAt = this.args.entry.signedInAt;

    // if the selected date is not passed in, we are looking at a range and want to see dates
    if (!selectedDate) return true;

    return !isSameDay(signedInAt, selectedDate);
  }

  get showOutDate(): boolean {
    const selectedDate = this.args.selectedDate;
    const signedOutAt = this.args.entry.signedOutAt;

    // if the selected date is not passed in, we are looking at a range and want to see dates
    if (!selectedDate) return true;

    return !isSameDay(signedOutAt, selectedDate);
  }

  get isForVisitor(): boolean {
    return this.args.userType === 'visitor';
  }

  get showEditOption(): boolean {
    const featureEnabled = this.abilities.can('see features for dbeam');
    return featureEnabled && !this.isForVisitor && !this.entry.signedInAt && this.abilities.can('edit reservation');
  }

  get showLegacyReservationOptions(): boolean {
    const featureFlagEnabled = this.abilities.can('see features for dbeam');
    return !featureFlagEnabled || this.isForVisitor;
  }

  get canDeleteReservation(): boolean {
    const featureFlagEnabled = this.abilities.can('see features for dbeam');
    return featureFlagEnabled && !this.isForVisitor && this.abilities.can('delete reservation');
  }

  get entry(): EntryModel {
    return this.args.entry;
  }

  get entryReservation(): ReservationModel | undefined {
    return this.args.entry.reservations?.content?.[0];
  }

  getUserDataField(entry: EntryModel, fieldName: string): string | undefined {
    const userData = entry.userData ?? [];
    const userDataField = userData.find((data) => data.field === fieldName);
    return userDataField?.value;
  }

  @action
  trackOpenActionMenu(entry_id: string): void {
    this.metrics.trackEvent('Entry Overflow Opened', { entry_id });
  }

  trackReprintBadgeRequested(entry: EntryModel): void {
    const entry_id = parseInt(entry.id, 10);
    const properties = { action_origin: 'visitor_log', entry_id };
    this.metrics.trackEvent('Reprint Entry Badge Requested', properties);
  }

  @action
  trackClickEntryDetail(entryId: string): void {
    this.metrics.trackEvent('Entry Details Clicked', { entry_id: entryId });
  }

  @action
  referToEnvoy(): void {
    this.windowLocation.assign(this.referralMailtoLink);
  }

  @action
  reprintBadge(entry: EntryModel, printer: PrinterModel): void {
    this.args.reprintBadge(entry, printer);
    this.trackReprintBadgeRequested(entry);
  }

  @action
  inviteAgain(entry: EntryModel): void {
    const { email, fullName: name } = entry;
    const previousInvite = entry.belongsTo('invite').id();

    void this.router.transitionTo('visitors.invites.new', {
      queryParams: { email, name, previousInvite, returnTo: 'visitors.entries' },
    });

    this.metrics.trackEvent('Entries Invited Again', { invite_id: previousInvite });
  }

  @dropTask
  reservationModalTask: {
    perform(): Generator<Promise<unknown>, unknown, unknown>;
  } = {
    *perform(this: { context: EntryFeedItemComponent; abort?: () => void; continue?: () => void }) {
      this.context.workplaceMetrics.trackEvent('EMPLOYEE_LOG_EDIT_RESERVATION_LINK_CLICKED', {
        entryId: this.context.entry?.id,
        reservationId: this.context.entryReservation?.id,
      });

      const deferred = defer();
      this.abort = () => {
        this.context.workplaceMetrics.trackEvent('EMPLOYEE_LOG_EDIT_RESERVATION_FLOW_ABORTED', {
          entryId: this.context.entry?.id,
          reservationId: this.context.entryReservation?.id,
        });

        deferred.resolve(false);
      };
      this.continue = () => {
        void this.context.args.loadEntries.perform();

        this.context.workplaceMetrics.trackEvent('EMPLOYEE_LOG_EDIT_RESERVATION_FLOW_COMPLETED', {
          entryId: this.context.entry?.id,
          reservationId: this.context.entryReservation?.id,
        });

        deferred.resolve(true);
      };
      return yield deferred.promise;
    },
  };
}
